import { Link } from "react-router";
import type { StudentSkillBelow, StudentSkillRootRow, StudentSkillRow } from "@edu/shared";
import {
  BELOW_ACTION,
  BELOW_HEAD,
  BELOW_NO_TASKS,
  GRADE_UNKNOWN,
  capitalized,
  confidenceWord,
  misconceptionCountText,
  prereqKindText,
  rootPhrase,
  wrongText,
} from "./student-skills-text.ts";
import { SkillLine } from "./StudentSkillsRow.tsx";

/**
 * Найденное место: одна фраза обычными словами, числа, названная ошибка и то,
 * что лежит под ним (7.1 проекта).
 *
 * ⚠️ **Подпись блока приезжает ДАННЫМИ (`rootWord`), а не стоит литералом.** У
 * математики это «корень», у русского будет «причина»: преподаватель русского
 * читает «корень» как морфему (3.3 проекта).
 *
 * ⚠️ **Название ошибки и рецепт показываются только у корня**, а не у каждой
 * строки. У следствий ошибки тоже есть, но показать их значит предложить
 * чинить следствие (решение 4 раскладки 7.1).
 *
 * ⚠️ Ни графа, ни словаря мисконцепций здесь нет: `label`, `remedy` и текст
 * `why` у ребра приезжают ручкой готовыми словами (1.4 и 7.5 проекта).
 */
export function SkillsRoot(props: {
  root: StudentSkillRootRow;
  /** Строка самого корня из `skills`: название, класс и его ошибки. */
  row: StudentSkillRow | undefined;
  /** Названия проваленных тем, из которых спуск пришёл в этот корень. */
  fromNames: readonly string[];
  rootWord: string;
  studentUserId: string;
}) {
  const { root, row } = props;
  const name = row?.name ?? root.skill;

  return (
    <article className="gaps__root">
      <p className="gaps__word">{capitalized(props.rootWord)}</p>

      {/* Первой строкой одно предложение обычными словами, а не список: это и
          есть товар, за которым сюда пришли */}
      <p className="gaps__lead">{rootPhrase(name, props.fromNames)}</p>

      <ul className="list list--gaps">
        {row === undefined ? (
          // Строки корня в ответе не оказалось: числа всё равно приехали в
          // самом корне, и показать их обязаны, иначе вердикт останется без
          // знаменателя
          <li className="row">
            <span className="cell row__name">{name}</span>
            <span className="cell row__void" data-label="Класс">
              {GRADE_UNKNOWN}
            </span>
            <span className="cell" data-label="Ошибки">
              {wrongText(root.wrong, root.tasks)}
            </span>
            <span className="cell row__void" data-label="Надёжность">
              {confidenceWord(root.confidence)}
            </span>
          </li>
        ) : (
          <SkillLine row={row} />
        )}
      </ul>

      {row !== undefined && row.misconceptions.length > 0 && (
        <ul className="gaps__mis">
          {row.misconceptions.map((m) => (
            <li key={m.id}>
              <span className="gaps__label">{m.label}</span>{" "}
              <span className="muted">{misconceptionCountText(m.tasks, row.tasks)}</span>
              <span className="gaps__remedy">{m.remedy}</span>
            </li>
          ))}
        </ul>
      )}

      {root.below.length > 0 && (
        <div className="gaps__below">
          {/*
            Блок обязателен и это не отладка: у семи тем дна графа генераторов
            нет ни у одной, то есть настоящее дно почти всегда ниже того, что мы
            умеем спросить. Преподавателю это даёт готовый вопрос на урок вместо
            обещания (3.2 проекта).
          */}
          <p className="gaps__belowhead">{BELOW_HEAD}</p>
          {root.below.map((b) => (
            <BelowLine key={b.skill} below={b} studentUserId={props.studentUserId} />
          ))}
        </div>
      )}
    </article>
  );
}

/**
 * Тема под корнем: то, чего мы не измеряли.
 *
 * ⚠️ **Кнопка есть только там, где есть задания** (`covered`). Кнопка, ведущая в
 * отказ «генераторов нет», хуже отсутствия кнопки, поэтому у непокрытой темы
 * стоит честная подпись без обещаний и без даты.
 */
function BelowLine(props: { below: StudentSkillBelow; studentUserId: string }) {
  const b = props.below;

  return (
    <div className="gaps__belowrow">
      {/* Текст слева одним узлом, действие справа: до 14.09.2026 это была стопка
          из четырёх абзацев на каждую тему, и трёх тем подряд хватало, чтобы
          панель стала вдвое плотнее всего остального на экране */}
      <div className="gaps__belowtext">
        <p className="gaps__belowname">
          {b.name}
          <span className="muted"> {b.grade ?? GRADE_UNKNOWN}</span>
        </p>
        {/* Текст ребра слово в слово из ответа: он написан для преподавателя */}
        <p className="muted gaps__why">{b.why}</p>
        <p className="muted gaps__kind">{prereqKindText(b.kind)}</p>
      </div>
      {b.covered ? (
        /*
          ⚠️ Сегодня кнопка ведёт в обычную форму выдачи, без предзаполненной
          темы: второй вход в форму (`?picks=`) это следующий шаг и отдельная
          работа. Обещание при этом честное: задания по этой теме у платформы
          действительно есть, и задать их можно прямо отсюда.
        */
        <Link className="btn btn--sm" to={`/homework/new?studentId=${props.studentUserId}`}>
          {BELOW_ACTION}
        </Link>
      ) : (
        <p className="muted">{BELOW_NO_TASKS}</p>
      )}
    </div>
  );
}
